/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getDoctorTimeTable, getTimeslots } from '../../api';
import { formatFullDate, sortBy } from '../../utils';

// eslint-disable-next-line consistent-return
export const fetchTimeslots = createAsyncThunk(
    'doctors/fetchTimeslots',
    // eslint-disable-next-line consistent-return
    async ({
        branch,
        date,
        specializationId,
        baseUrl,
        // eslint-disable-next-line consistent-return
    }, { rejectWithValue }) => {
        try {
            const response = await (await getTimeslots(branch, date, specializationId))(baseUrl);

            return {
                data: response?.data?.map?.(item => ({
                    ...item,
                    freeTimeslots: sortBy(item?.freeTimeslots, 'timeslotId'),
                })),
                branch,
                specializationId,
                date,
            };
        } catch (err) {
            rejectWithValue([], err);
        }
    },
);

export const fetchTimetable = createAsyncThunk(
    'doctors/fetchTimetable',
    // eslint-disable-next-line consistent-return
    async ({
        assignment,
        doctorId,
        dateStart,
        dateEnd,
        baseUrl,
    }, { rejectWithValue }) => {
        try {
            const response = await (await getDoctorTimeTable(assignment, dateStart, dateEnd))(baseUrl);
            const data = response?.data ?? [];
            const allData = new Set([...data.map(item => formatFullDate(item?.startTime))]);
            return {
                data: Array.from(allData),
                allData: data,
                assignment,
                doctorId,
                dateStart,
                dateEnd,
            };
        } catch (err) {
            rejectWithValue(
                {
                    assignment,
                    doctorId,
                    dateStart,
                    dateEnd,
                    data: [],
                },
                err,
            );
            return {};
        }
    },
);

export const initState = {
    timeslots: [],
    timetable: {},
    isLoading: false,
    error: null,
    timeslotsBranch: null,
    timeslotsSpeciality: null,
    timeslotDate: null,
    timetableData: {},
};

export const doctorsSlice = createSlice({
    name: 'doctors',
    initialState: { ...initState },
    reducers: {
        clearTimeslotRequestedParams: (state) => {
            state.timeslotsBranch = null;
            state.timeslotsSpeciality = null;
            state.timeslotDate = null;
            state.timetableLoaded = undefined;
        },
        clearTimetableRequestParams: (state) => {
            state.timetableLoaded = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTimeslots.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchTimeslots.fulfilled, (state, action) => {
                if (action.payload?.data) {
                    // eslint-disable-next-line camelcase
                    const newDoctors = [...action.payload?.data].map(({
                        children_age,
                        adult_age,
                        children_age_to,
                        adult_age_to,
                        ...rest
                    }) => ({
                        ...rest,
                        childrenAge: children_age,
                        childrenAgeTo: children_age_to,
                        adultAge: adult_age,
                        adultAgeTo: adult_age_to,
                        children_age,
                        adult_age,
                        children_age_to,
                        adult_age_to,
                    }));
                    state.timeslots = [...newDoctors];
                }
                state.timeslotsBranch = action.payload?.branch;
                state.timeslotsSpeciality = action.payload?.specializationId;
                state.timeslotDate = action.payload?.date;
                state.isLoading = false;
                state.error = null;
            })
            .addCase(fetchTimeslots.rejected, (state, { error }) => {
                state.timeslots = [];
                state.isLoading = false;
                state.error = error;
            })
            .addCase(fetchTimetable.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchTimetable.rejected, (state, {
                error,
                payload,
            }) => {
                state.timetable = {
                    ...state.timetable,
                    [payload.doctorId]: [],
                };
                state.isLoading = false;
                state.error = error;
            })
            .addCase(fetchTimetable.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = null;
                state.timetableData = { [action.payload.doctorId]: action.payload?.allData };
                // state.timetableLoaded = `${action?.payload?.assignment};${action?.payload?.doctorId};${action?.payload?.dateStart};${action?.payload?.dateEnd}`;
                state.timetableLoaded = `${action?.payload?.doctorId};${action?.payload?.dateStart};${action?.payload?.dateEnd}`;
                state.timetable.assignment = action.payload.assignment;
                state.timetable = {
                    ...state.timetable,
                    [action.payload.doctorId]: action.payload.data ?? [],
                };
            });
    },
});

export const {
    clearTimeslotRequestedParams,
    clearTimetableRequestParams,
} = doctorsSlice.actions;

export default doctorsSlice.reducer;
